import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { volunteerEventsApi } from '../../services/api';
import { RichTextEditor } from './RichTextEditor';
import { MeetingPointPicker } from './MeetingPointPicker';
import { RowActionsMenu, RowAction } from './RowActionsMenu';
import {
  VolunteerEventDto,
  SaveVolunteerEventRequest,
  RosterEntryDto,
  VolunteerActivityEntryDto,
  VolunteerActivityAction,
  VolunteerActorType,
  ACTIVITY_ACTION_LABELS,
  VolunteerEventFormat,
  VolunteerEventType,
  VolunteerEventDifficulty,
  VolunteerEventStatus,
  VolunteerRegistrationStatus,
  EVENT_FORMAT_LABELS,
  EVENT_TYPE_LABELS,
  DIFFICULTY_LABELS,
  EVENT_STATUS_LABELS,
  REGISTRATION_STATUS_LABELS,
  NZ_REGIONS,
} from '../../types/VolunteerEvent';

const emptyForm: SaveVolunteerEventRequest = {
  title: '',
  summary: '',
  description: '',
  format: VolunteerEventFormat.ScheduledCrew,
  eventType: VolunteerEventType.SingleDay,
  difficulty: VolunteerEventDifficulty.Easy,
  startDate: '',
  endDate: '',
  region: '',
  capacity: 10,
  meetingLocationDescription: '',
  latitude: undefined,
  longitude: undefined,
  leaderName: '',
  leaderContact: '',
  whatToBring: '',
  registrationOpensAt: '',
  registrationClosesAt: '',
  imageUrl: '',
  externalCtaUrl: '',
  externalCtaLabel: '',
};

// datetime-local <-> ISO helpers (inputs work in local time, API in ISO)
const toLocalInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toIso = (local?: string | null): string | undefined =>
  local ? new Date(local).toISOString() : undefined;

/**
 * Real name first, then the profile's display name — which is alias-capable and
 * may be an email local-part or the literal "Member", so it's a last resort
 * rather than the primary. Registrations taken before the name snapshot existed
 * whose owner has no profile genuinely have no name on file anywhere.
 */
const rosterName = (entry: RosterEntryDto): string =>
  `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim()
  || entry.displayName
  || 'No name on file';

const formatActivityTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const parseDetails = (entry: VolunteerActivityEntryDto): Record<string, unknown> | null => {
  if (!entry.details) return null;
  try {
    return JSON.parse(entry.details) as Record<string, unknown>;
  } catch {
    return null;
  }
};

/** Rows the migration reconstructed from registration timestamps, before the log existed. */
const isBackfilled = (entry: VolunteerActivityEntryDto): boolean => parseDetails(entry)?.backfilled === true;

/**
 * Label for an entry. Backfilled "Registered" rows are ambiguous: a volunteer
 * who was waitlisted and then cancelled before the log existed has no
 * previous-status column, so we say "registered" rather than "confirmed".
 */
const activityLabel = (entry: VolunteerActivityEntryDto): string => {
  if (entry.action === VolunteerActivityAction.Registered && isBackfilled(entry)) {
    return 'Registered (confirmed or waitlisted — not recorded)';
  }
  return ACTIVITY_ACTION_LABELS[entry.action] ?? `Action ${entry.action}`;
};

/** Human line for the action-specific JSON the API stores alongside an entry. */
const activityDetail = (entry: VolunteerActivityEntryDto): string | null => {
  const d = parseDetails(entry);
  if (!d) return null;
  if (entry.action === VolunteerActivityAction.CapacityChanged) {
    return `${d.previousCapacity ?? '?'} → ${d.newCapacity ?? '?'}`;
  }
  const parts: string[] = [];
  if (typeof d.reason === 'string' && d.reason) parts.push(`Reason: ${d.reason}`);
  if (typeof d.previousStatus === 'string') parts.push(`Was ${String(d.previousStatus).toLowerCase()}`);
  if (d.backfilled) parts.push('Reconstructed from registration dates');
  return parts.length ? parts.join(' · ') : null;
};

const activityDotClass = (action: VolunteerActivityAction): string => {
  switch (action) {
    case VolunteerActivityAction.Registered:
    case VolunteerActivityAction.PromotedFromWaitlist:
    case VolunteerActivityAction.EventPublished:
      return 'bg-emerald-500';
    case VolunteerActivityAction.Waitlisted:
    case VolunteerActivityAction.CapacityChanged:
      return 'bg-orange-400';
    case VolunteerActivityAction.CancelledBySelf:
    case VolunteerActivityAction.RemovedByAdmin:
    case VolunteerActivityAction.EventCancelled:
    case VolunteerActivityAction.EventDeleted:
      return 'bg-red-500';
    default:
      return 'bg-gray-400';
  }
};

const statusBadgeClass = (status: VolunteerEventStatus): string => {
  switch (status) {
    case VolunteerEventStatus.Published: return 'bg-emerald-100 text-emerald-800';
    case VolunteerEventStatus.Draft: return 'bg-gray-100 text-gray-800';
    case VolunteerEventStatus.Cancelled: return 'bg-red-100 text-red-800';
    case VolunteerEventStatus.Completed: return 'bg-blue-100 text-blue-800';
  }
};

const VolunteerEventsPage: React.FC = () => {
  const [events, setEvents] = useState<VolunteerEventDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalError, setModalError] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<VolunteerEventDto | null>(null);
  const [formData, setFormData] = useState<SaveVolunteerEventRequest>(emptyForm);
  const [saving, setSaving] = useState(false);

  const [rosterEvent, setRosterEvent] = useState<VolunteerEventDto | null>(null);
  const [roster, setRoster] = useState<RosterEntryDto[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [rosterTab, setRosterTab] = useState<'roster' | 'activity'>('roster');
  const [activity, setActivity] = useState<VolunteerActivityEntryDto[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Search debounce: one request 300ms after typing stops, not per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Monotonic sequence discards out-of-order responses from slow requests.
  const requestSeq = useRef(0);

  const loadEvents = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      const response = await volunteerEventsApi.getEvents({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        region: selectedRegion || undefined,
        status: selectedStatus === '' ? undefined : Number(selectedStatus),
        includeDeleted,
      });
      if (seq !== requestSeq.current) return;
      setEvents(response.events);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
      setAccessDenied(false);
    } catch (error: any) {
      if (seq !== requestSeq.current) return;
      if (error?.response?.status === 403) {
        setAccessDenied(true);
      }
      console.error('Failed to load volunteer events:', error);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, selectedRegion, selectedStatus, includeDeleted]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRegion('');
    setSelectedStatus('');
    setIncludeDeleted(false);
    setPage(1);
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setFormData(emptyForm);
    setModalError(null);
    setShowModal(true);
  };

  const handleEdit = (event: VolunteerEventDto) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      summary: event.summary ?? '',
      description: event.description ?? '',
      format: event.format,
      eventType: event.eventType,
      difficulty: event.difficulty,
      startDate: toLocalInput(event.startDate),
      endDate: toLocalInput(event.endDate),
      region: event.region,
      capacity: event.capacity ?? 10,
      meetingLocationDescription: event.meetingLocationDescription ?? '',
      latitude: event.latitude,
      longitude: event.longitude,
      leaderName: event.leaderName ?? '',
      leaderContact: event.leaderContact ?? '',
      whatToBring: event.whatToBring ?? '',
      registrationOpensAt: toLocalInput(event.registrationOpensAt),
      registrationClosesAt: toLocalInput(event.registrationClosesAt),
      imageUrl: event.imageUrl ?? '',
      externalCtaUrl: event.externalCtaUrl ?? '',
      externalCtaLabel: event.externalCtaLabel ?? '',
    });
    setModalError(null);
    setShowModal(true);
  };

  // Copy an existing event into a fresh Draft — same fields, no editingEvent, so
  // handleSave routes to createEvent (new Id). Speeds up adding near-identical
  // crews; the user just adjusts dates/region and saves.
  const handleDuplicate = (event: VolunteerEventDto) => {
    setEditingEvent(null);
    setFormData({
      title: `${event.title} (Copy)`,
      summary: event.summary ?? '',
      description: event.description ?? '',
      format: event.format,
      eventType: event.eventType,
      difficulty: event.difficulty,
      startDate: toLocalInput(event.startDate),
      endDate: toLocalInput(event.endDate),
      region: event.region,
      capacity: event.capacity ?? 10,
      meetingLocationDescription: event.meetingLocationDescription ?? '',
      latitude: event.latitude,
      longitude: event.longitude,
      leaderName: event.leaderName ?? '',
      leaderContact: event.leaderContact ?? '',
      whatToBring: event.whatToBring ?? '',
      registrationOpensAt: toLocalInput(event.registrationOpensAt),
      registrationClosesAt: toLocalInput(event.registrationClosesAt),
      imageUrl: event.imageUrl ?? '',
      externalCtaUrl: event.externalCtaUrl ?? '',
      externalCtaLabel: event.externalCtaLabel ?? '',
    });
    setModalError(null);
    setShowModal(true);
  };

  const isCrewFormat = formData.format === VolunteerEventFormat.ScheduledCrew;
  const isApplicationFormat = formData.format === VolunteerEventFormat.Application;

  const validateForm = (): string | null => {
    if (!formData.title || !formData.region) {
      return 'Title and region are required.';
    }
    // Dates, capacity and the registration window only apply to scheduled crews.
    if (isCrewFormat) {
      if (!formData.startDate || !formData.endDate) {
        return 'Scheduled crews need a start and end date.';
      }
      if (new Date(formData.endDate) < new Date(formData.startDate)) {
        return 'End date must not be before the start date.';
      }
      if (!formData.capacity || formData.capacity < 1) {
        return 'Scheduled crews need a capacity of at least 1.';
      }
      if (
        formData.registrationOpensAt && formData.registrationClosesAt &&
        new Date(formData.registrationClosesAt) < new Date(formData.registrationOpensAt)
      ) {
        return 'Registration close must not be before registration open.';
      }
    }
    if (isApplicationFormat && !formData.externalCtaUrl?.trim()) {
      return "An application role needs an 'Apply' link.";
    }
    if ((formData.latitude === undefined) !== (formData.longitude === undefined)) {
      return 'Latitude and longitude must be provided together.';
    }
    if (formData.latitude !== undefined && (formData.latitude < -90 || formData.latitude > 90)) {
      return 'Latitude must be between -90 and 90.';
    }
    if (formData.longitude !== undefined && (formData.longitude < -180 || formData.longitude > 180)) {
      return 'Longitude must be between -180 and 180.';
    }
    return null;
  };

  /**
   * CiviCRM's TA Volunteers searches link each crew straight here as
   * ?event=<id>, where the id is Volunteer_Crew.Portal_Event_ID - the portal's
   * own event id, which the sync writes. Fetched by id rather than looked for
   * in the loaded page, because the crew being opened is usually not on the
   * page the current filters happen to show.
   *
   * Declared after handleEdit on purpose: the effect calls it.
   */
  useEffect(() => {
    const id = searchParams.get('event');
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const event = await volunteerEventsApi.getEvent(id);
        if (cancelled) return;
        handleEdit(event);
        // Consumed: a refresh after closing should not reopen it.
        setSearchParams({}, { replace: true });
      } catch (err) {
        if (cancelled) return;
        // A 403 is already reported by the access panel below; saying "it may
        // have been deleted" beside it would contradict it.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status !== 403) {
          setDeepLinkError(status === 404
            ? `That crew (${id}) no longer exists. It may have been deleted.`
            : `Could not open crew ${id}. The server did not respond as expected.`);
        }
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Runs once: the link is how you arrived, not something that changes after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    const clientError = validateForm();
    if (clientError) {
      setModalError(clientError);
      return;
    }
    setModalError(null);
    try {
      setSaving(true);
      // Dates/capacity/registration window are crew-only; send them null for
      // the other formats so a format switch clears stale values server-side.
      const payload: SaveVolunteerEventRequest = {
        ...formData,
        startDate: isCrewFormat ? toIso(formData.startDate) ?? null : null,
        endDate: isCrewFormat ? toIso(formData.endDate) ?? null : null,
        capacity: isCrewFormat ? formData.capacity : null,
        registrationOpensAt: isCrewFormat ? toIso(formData.registrationOpensAt) : undefined,
        registrationClosesAt: isCrewFormat ? toIso(formData.registrationClosesAt) : undefined,
        latitude: formData.latitude === undefined || Number.isNaN(formData.latitude) ? undefined : formData.latitude,
        longitude: formData.longitude === undefined || Number.isNaN(formData.longitude) ? undefined : formData.longitude,
      };
      if (editingEvent) {
        await volunteerEventsApi.updateEvent(editingEvent.id, payload);
      } else {
        await volunteerEventsApi.createEvent(payload);
      }
      setShowModal(false);
      await loadEvents();
    } catch (error: any) {
      console.error('Failed to save volunteer event:', error);
      // Surface ASP.NET ValidationProblemDetails field errors when present.
      const problemErrors = error.response?.data?.errors;
      if (problemErrors && typeof problemErrors === 'object') {
        const messages = Object.values(problemErrors).flat().filter((m): m is string => typeof m === 'string');
        setModalError(messages.join(' ') || 'Validation failed.');
      } else {
        setModalError(error.response?.data?.message || 'Failed to save the event.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (event: VolunteerEventDto) => {
    if (!window.confirm(`Publish "${event.title}"? It becomes publicly visible.`)) return;
    try {
      await volunteerEventsApi.publishEvent(event.id);
      await loadEvents();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to publish the event.');
    }
  };

  const handleCancel = async (event: VolunteerEventDto) => {
    if (!window.confirm(`Cancel "${event.title}"? Registrants are not notified automatically (MVP).`)) return;
    try {
      await volunteerEventsApi.cancelEvent(event.id);
      await loadEvents();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to cancel the event.');
    }
  };

  const handleDelete = async (event: VolunteerEventDto) => {
    if (!window.confirm(`Delete "${event.title}"? This is a soft delete.`)) return;
    try {
      await volunteerEventsApi.deleteEvent(event.id);
      await loadEvents();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete the event.');
    }
  };

  const loadRoster = async (event: VolunteerEventDto, includeCancelled: boolean) => {
    setRosterLoading(true);
    try {
      const response = await volunteerEventsApi.getRoster(event.id, includeCancelled);
      setRoster(response.roster);
    } catch (error) {
      console.error('Failed to load roster:', error);
      setRoster([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const loadActivity = async (event: VolunteerEventDto) => {
    setActivityLoading(true);
    try {
      const response = await volunteerEventsApi.getActivity(event.id);
      setActivity(response.activity);
      setActivityLoaded(true);
    } catch (error) {
      console.error('Failed to load activity:', error);
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const openRoster = async (event: VolunteerEventDto) => {
    setRosterEvent(event);
    setRosterTab('roster');
    setShowRemoved(false);
    setActivity([]);
    setActivityLoaded(false);
    await loadRoster(event, false);
  };

  const toggleShowRemoved = async (checked: boolean) => {
    setShowRemoved(checked);
    if (rosterEvent) await loadRoster(rosterEvent, checked);
  };

  const switchRosterTab = async (tab: 'roster' | 'activity') => {
    setRosterTab(tab);
    // Lazy: most opens never look at the history.
    if (tab === 'activity' && rosterEvent && !activityLoaded) await loadActivity(rosterEvent);
  };

  const handleRemoveRegistrant = async (entry: RosterEntryDto) => {
    if (!rosterEvent) return;
    // Same native-dialog convention as the other destructive actions on this
    // page; prompt() doubles as the confirm (Cancel returns null = abort).
    const reason = window.prompt(
      `Remove ${rosterName(entry)} from "${rosterEvent.title}"?\n\n` +
      (entry.status === VolunteerRegistrationStatus.Confirmed
        ? 'Their confirmed spot goes to the first person on the waitlist.\n\n'
        : 'They will leave the waitlist.\n\n') +
      'Optional reason (shown on the roster and in the activity log):',
      '',
    );
    if (reason === null) return;

    setRemovingId(entry.registrationId);
    try {
      const result = await volunteerEventsApi.removeRegistrant(rosterEvent.id, entry.registrationId, reason.trim());
      await Promise.all([
        loadRoster(rosterEvent, showRemoved),
        loadEvents(),
        activityLoaded ? loadActivity(rosterEvent) : Promise.resolve(),
      ]);
      if (result.promotedRegistrationId) {
        alert('Removed. The first person on the waitlist has been moved up to confirmed — remember to let them know.');
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to remove the volunteer.');
    } finally {
      setRemovingId(null);
    }
  };

  // The drawer is modal: Escape closes it and the page behind stops scrolling.
  useEffect(() => {
    if (!rosterEvent) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRosterEvent(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [rosterEvent]);

  // Counted from the loaded roster rather than rosterEvent, which is a snapshot
  // taken when the drawer opened and goes stale after a removal.
  const confirmedCount = roster.filter(r => r.status === VolunteerRegistrationStatus.Confirmed).length;
  const waitlistedCount = roster.filter(r => r.status === VolunteerRegistrationStatus.Waitlisted).length;

  const secondaryActions = (event: VolunteerEventDto): RowAction[] => {
    const items: RowAction[] = [{ label: 'Duplicate', onSelect: () => handleDuplicate(event) }];
    if (event.status === VolunteerEventStatus.Draft || event.status === VolunteerEventStatus.Published) {
      items.push({ label: 'Cancel event', onSelect: () => handleCancel(event), destructive: true });
    }
    if (!event.isDeleted) {
      items.push({ label: 'Delete', onSelect: () => handleDelete(event), destructive: true });
    }
    return items;
  };

  const formatDateRange = (event: VolunteerEventDto): string => {
    // Non-crew formats have no dates — they run "Anytime".
    if (!event.startDate || !event.endDate) return 'Anytime';
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    const fmt: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    const sameDay = start.toDateString() === end.toDateString();
    return sameDay
      ? start.toLocaleDateString('en-NZ', fmt)
      : `${start.toLocaleDateString('en-NZ', fmt)} – ${end.toLocaleDateString('en-NZ', fmt)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 text-left">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <Link to="/" className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">
              ← Back to Dashboard
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">Volunteer Events</h1>
            {/* A link from CiviCRM that led nowhere used to look like the page
                simply loading normally, which is worse than saying so. */}
            {deepLinkError && (
              <p role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {deepLinkError}
              </p>
            )}
            <p className="text-gray-600 mt-1">Create, publish and manage volunteer work parties</p>
          </div>
          <button
            onClick={handleCreate}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium"
          >
            + New Event
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Search title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px]"
            />
            <select
              value={selectedRegion}
              onChange={(e) => { setSelectedRegion(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All regions</option>
              {NZ_REGIONS.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
            <select
              value={selectedStatus}
              onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              {Object.entries(EVENT_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeDeleted}
                onChange={(e) => { setIncludeDeleted(e.target.checked); setPage(1); }}
              />
              Include deleted
            </label>
            <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700">
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {accessDenied ? (
            <div className="p-12 text-center">
              <p className="text-gray-900 font-semibold">You don't have access to Volunteer Events</p>
              <p className="text-gray-500 text-sm mt-1">
                Membership of the TrailApp-Admin-VolunteerEvents group is required — contact an administrator.
              </p>
            </div>
          ) : loading ? (
            <div className="p-12 text-center text-gray-500">Loading events…</div>
          ) : events.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No volunteer events found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Event', 'Dates', 'Region', 'Status', 'Capacity'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                    {/* Pinned to the right edge so the actions stay reachable when the table scrolls. */}
                    <th className="sticky right-0 bg-gray-50 px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {events.map(event => {
                    const canPublish = event.status === VolunteerEventStatus.Draft || event.status === VolunteerEventStatus.Cancelled;
                    return (
                      <tr key={event.id} className={`group ${event.isDeleted ? 'text-gray-400' : ''}`}>
                        <td className="px-4 py-3 min-w-[16rem] group-hover:bg-gray-50">
                          <div className={`font-medium ${event.isDeleted ? 'line-through' : 'text-gray-900'}`}>{event.title}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {EVENT_FORMAT_LABELS[event.format]} · {EVENT_TYPE_LABELS[event.eventType]} · {DIFFICULTY_LABELS[event.difficulty]}
                            {event.isDeleted ? ' · deleted' : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap group-hover:bg-gray-50">{formatDateRange(event)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap group-hover:bg-gray-50">{event.region}</td>
                        <td className="px-4 py-3 group-hover:bg-gray-50">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusBadgeClass(event.status)}`}>
                            {EVENT_STATUS_LABELS[event.status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap group-hover:bg-gray-50">
                          {event.capacity == null
                            ? '—'
                            : <>{event.confirmedCount}/{event.capacity}</>}
                          {event.waitlistCount > 0 && (
                            <div className="text-xs text-orange-700">+{event.waitlistCount} waitlisted</div>
                          )}
                        </td>
                        <td className="sticky right-0 bg-white px-4 py-3 group-hover:bg-gray-50 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">
                          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                            {canPublish && (
                              <button
                                onClick={() => handlePublish(event)}
                                className="h-8 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700"
                              >
                                Publish
                              </button>
                            )}
                            <button
                              onClick={() => openRoster(event)}
                              className="h-8 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Roster
                            </button>
                            <button
                              onClick={() => handleEdit(event)}
                              className="h-8 rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Edit
                            </button>
                            <RowActionsMenu label={`More actions for ${event.title}`} items={secondaryActions(event)} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm">
              <span className="text-gray-500">{totalCount} events</span>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
                >
                  Previous
                </button>
                <span className="text-gray-700">Page {page} of {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1 rounded border border-gray-300 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingEvent ? 'Edit Event' : 'New Event'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Format *</label>
                <select value={formData.format}
                  onChange={(e) => setFormData({ ...formData, format: Number(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(EVENT_FORMAT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  {isCrewFormat
                    ? 'Dated work party with a capacity and waitlist. Volunteers register for a spot.'
                    : isApplicationFormat
                      ? 'A posting for a single selected role. Volunteers apply via an external link — no in-app registration.'
                      : 'Anytime opportunity with no set date or capacity. Volunteers register so you know who is helping.'}
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={200} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
                <input type="text" value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={500} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <RichTextEditor
                  value={formData.description ?? ''}
                  onChange={(v) => setFormData({ ...formData, description: v })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={formData.eventType}
                  onChange={(e) => setFormData({ ...formData, eventType: Number(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: Number(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(DIFFICULTY_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              {isCrewFormat && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
                    <input type="datetime-local" value={formData.startDate ?? ''}
                      onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End *</label>
                    <input type="datetime-local" value={formData.endDate ?? ''}
                      onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Region *</label>
                <select value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">Select region…</option>
                  {NZ_REGIONS.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>
              {isCrewFormat && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Capacity *</label>
                  <input type="number" min={1} value={formData.capacity ?? ''}
                    onChange={(e) => setFormData({ ...formData, capacity: Number(e.target.value) })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting location description</label>
                <input type="text" value={formData.meetingLocationDescription}
                  onChange={(e) => setFormData({ ...formData, meetingLocationDescription: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={500} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Meeting point (click the map)</label>
                <MeetingPointPicker
                  latitude={formData.latitude}
                  longitude={formData.longitude}
                  onChange={(lat, lng) => setFormData({ ...formData, latitude: lat, longitude: lng })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leader name</label>
                <input type="text" value={formData.leaderName}
                  onChange={(e) => setFormData({ ...formData, leaderName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={200} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Leader contact (confirmed registrants only)</label>
                <input type="text" value={formData.leaderContact}
                  onChange={(e) => setFormData({ ...formData, leaderContact: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={200} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">What to bring</label>
                <RichTextEditor
                  value={formData.whatToBring ?? ''}
                  onChange={(v) => setFormData({ ...formData, whatToBring: v })}
                />
              </div>
              {isCrewFormat && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration opens (optional)</label>
                    <input type="datetime-local" value={formData.registrationOpensAt}
                      onChange={(e) => setFormData({ ...formData, registrationOpensAt: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration closes (optional)</label>
                    <input type="datetime-local" value={formData.registrationClosesAt}
                      onChange={(e) => setFormData({ ...formData, registrationClosesAt: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </>
              )}
              {!isCrewFormat && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      External link{isApplicationFormat ? ' *' : ''}
                    </label>
                    <input type="url" value={formData.externalCtaUrl}
                      onChange={(e) => setFormData({ ...formData, externalCtaUrl: e.target.value })}
                      placeholder="https://…  or  mailto:volunteer@…"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={500} />
                    <p className="mt-1 text-xs text-gray-400">
                      {isApplicationFormat
                        ? 'Where applicants apply — an application form or email address.'
                        : 'Optional safety / health & safety form volunteers should complete (e.g. a Google Form).'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link button label (optional)</label>
                    <input type="text" value={formData.externalCtaLabel}
                      onChange={(e) => setFormData({ ...formData, externalCtaLabel: e.target.value })}
                      placeholder={isApplicationFormat ? 'Apply now' : 'Sign the safety form'}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={60} />
                  </div>
                </>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input type="url" value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://…"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" maxLength={500} />
                <p className="mt-1 text-xs text-gray-400">
                  Paste an image link — e.g. from your WordPress media library. (File upload coming later.)
                </p>
              </div>
            </div>
            {modalError && (
              <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{modalError}</p>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {saving ? 'Saving…' : editingEvent ? 'Save changes' : 'Create draft'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roster drawer */}
      {rosterEvent && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          role="dialog"
          aria-modal="true"
          aria-labelledby="roster-drawer-title"
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => setRosterEvent(null)} />
          <div className="relative flex h-full w-full max-w-6xl flex-col bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 pt-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 id="roster-drawer-title" className="text-2xl font-semibold text-gray-900">{rosterEvent.title}</h2>
                  <p className="mt-1 text-sm text-gray-600">
                    {formatDateRange(rosterEvent)} · {rosterEvent.region} · {EVENT_FORMAT_LABELS[rosterEvent.format]}
                  </p>
                </div>
                <button
                  onClick={() => setRosterEvent(null)}
                  aria-label="Close roster"
                  className="-mr-2 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-2xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  ×
                </button>
              </div>

              {!rosterLoading && (
                <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-gray-500">Confirmed</dt>
                    <dd className="font-semibold text-gray-900">
                      {confirmedCount}{rosterEvent.capacity != null ? ` of ${rosterEvent.capacity}` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-gray-500">Waitlisted</dt>
                    <dd className="font-semibold text-gray-900">{waitlistedCount}</dd>
                  </div>
                </dl>
              )}

              <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                <div className="flex gap-6" role="tablist">
                  {(['roster', 'activity'] as const).map(tab => (
                    <button
                      key={tab}
                      role="tab"
                      aria-selected={rosterTab === tab}
                      onClick={() => switchRosterTab(tab)}
                      className={`pb-3 text-sm font-medium border-b-2 -mb-px ${rosterTab === tab ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                      {tab === 'roster' ? 'Roster' : 'Activity'}
                    </button>
                  ))}
                </div>
                {rosterTab === 'roster' && (
                  <div className="flex items-center gap-4 pb-2">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input type="checkbox" checked={showRemoved} onChange={e => toggleShowRemoved(e.target.checked)} />
                      Show removed
                    </label>
                    <a
                      href={volunteerEventsApi.getRosterExportUrl(rosterEvent.id)}
                      className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      ⬇ Download CSV
                    </a>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {rosterTab === 'roster' && (
                rosterLoading ? (
                  <div className="text-gray-500 py-12 text-center">Loading roster…</div>
                ) : roster.length === 0 ? (
                  <div className="text-gray-500 py-12 text-center">No registrations yet.</div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-50 shadow-[inset_0_-1px_0_rgb(229,231,235)]">
                      <tr>
                        {['#', 'Volunteer', 'Phone', 'Status', 'Emergency contact', 'Notes'].map((h, i) => (
                          <th key={i} className={`py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 0 ? 'pl-6 pr-3' : 'px-3'}`}>{h}</th>
                        ))}
                        <th className="py-2.5 pl-3 pr-6"><span className="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {roster.map((entry, i) => {
                        const cancelled = entry.status === VolunteerRegistrationStatus.Cancelled;
                        return (
                          <tr key={entry.registrationId} className={`align-top ${cancelled ? 'bg-gray-50 text-gray-400' : 'hover:bg-gray-50'}`}>
                            <td className="py-3 pl-6 pr-3 text-gray-500 tabular-nums">{cancelled ? '—' : i + 1}</td>
                            <td className="px-3 py-3 min-w-[14rem]">
                              <div className={`font-medium ${cancelled ? 'line-through' : 'text-gray-900'}`}>{rosterName(entry)}</div>
                              <div className="text-xs text-gray-500">{entry.email}</div>
                              {cancelled && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {entry.cancelledBy === 'self' ? 'Cancelled themselves' : `Removed by ${entry.cancelledBy || 'admin'}`}
                                  {entry.cancelledAt ? ` · ${formatActivityTime(entry.cancelledAt)}` : ''}
                                  {entry.cancellationReason ? ` · ${entry.cancellationReason}` : ''}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-3 text-gray-700 whitespace-nowrap tabular-nums">{entry.phone || '—'}</td>
                            <td className="px-3 py-3">
                              <span className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${
                                cancelled ? 'bg-gray-100 text-gray-600'
                                : entry.status === VolunteerRegistrationStatus.Confirmed ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-orange-100 text-orange-800'}`}>
                                {REGISTRATION_STATUS_LABELS[entry.status]}
                                {entry.waitlistPosition ? ` #${entry.waitlistPosition}` : ''}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-gray-700 min-w-[10rem]">
                              <div>{entry.emergencyContactName || '—'}</div>
                              <div className="text-xs text-gray-500 whitespace-nowrap tabular-nums">{entry.emergencyContactPhone}</div>
                            </td>
                            <td className="px-3 py-3 text-gray-700 min-w-[12rem] max-w-xs whitespace-pre-wrap break-words">{entry.medicalOrDietaryNotes || '—'}</td>
                            <td className="py-3 pl-3 pr-6 text-right whitespace-nowrap">
                              {!cancelled && (
                                <button
                                  onClick={() => handleRemoveRegistrant(entry)}
                                  disabled={removingId === entry.registrationId}
                                  className="h-8 rounded-md border border-red-200 px-3 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                >
                                  {removingId === entry.registrationId ? 'Removing…' : 'Remove'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              )}

              {rosterTab === 'activity' && (
                activityLoading ? (
                  <div className="text-gray-500 py-8 text-center">Loading activity…</div>
                ) : activity.length === 0 ? (
                  <div className="text-gray-500 py-8 text-center">Nothing has happened on this event yet.</div>
                ) : (
                  <ul className="divide-y divide-gray-100 px-6 py-2 text-sm">
                    {activity.map(entry => {
                      const detail = activityDetail(entry);
                      return (
                        <li key={entry.id} className="py-2.5 flex gap-3">
                          <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${activityDotClass(entry.action)}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-900">
                              {entry.subjectName && <span className="font-medium">{entry.subjectName} · </span>}
                              {activityLabel(entry)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatActivityTime(entry.occurredAt)}
                              {entry.actorType === VolunteerActorType.Admin && entry.actorName ? ` · by ${entry.actorName}` : ''}
                              {entry.actorType === VolunteerActorType.System ? ' · automatic' : ''}
                              {detail ? ` · ${detail}` : ''}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerEventsPage;
